import styled, { css, up } from "@xstyled/styled-components";
import { useMemo, useState } from "react";

import { Spacing, Text } from "@otta/design";
import { Link } from "@otta/search/components/Link";
import { Company } from "@otta/search/schema";
import { Select } from "@otta/search/components/Input/Select";
import { groupBy } from "@otta/search/utils/collections";
import { useClientSide } from "@otta/search/hooks/useClientSide";

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: lg;
  margin-bottom: xl;

  ${up(
    "tablet",
    css`
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    `
  )}
`;

const JobTitle = styled(Text)`
  text-decoration: underline;
`;

export const shouldDisplayCompanyJobs = (
  company: CompanyJobsProps["company"]
) => {
  return company.numberLiveJobs > 0;
};

interface CompanyJobsProps {
  company: Pick<Company.Fragment, "numberLiveJobs" | "liveJobs">;
}

type Option = { label: string; value: Set<string> | null };

export const CompanyJobs = ({ company }: CompanyJobsProps) => {
  const isClient = useClientSide();

  const [selectedLocation, setSelectedLocation] =
    useState<Option["value"]>(null);

  const jobs = company.liveJobs.filter(
    job => job.formattedLocationPreferences.length > 0
  );

  const options: Option[] = useMemo(() => {
    const groups: Record<string, Option> = {
      _all_jobs: { label: "All locations", value: null },
    };
    for (const { id, formattedLocationPreferences } of jobs) {
      for (const { name } of formattedLocationPreferences) {
        if (!groups[name]) {
          groups[name] = { label: name, value: new Set([id]) };
        } else {
          groups[name].value?.add(id);
        }
      }
    }
    return Object.values(groups);
  }, [jobs]);

  const jobsByFunction = useMemo(
    () =>
      groupBy(
        jobs.filter(job => !selectedLocation || selectedLocation.has(job.id)),
        (job: Company.LiveJobs) => job.function?.value
      ),
    [jobs, selectedLocation]
  );

  if (!shouldDisplayCompanyJobs(company)) return null;

  const handleLocationChange = (option: Option | null) => {
    if (option) {
      setSelectedLocation(option.value);
    }
  };

  return (
    <div data-testid="company-jobs">
      <Header>
        <Text as="h2" bold size={1}>
          Job{company.numberLiveJobs > 1 ? "s" : ""} ({company.numberLiveJobs})
        </Text>
        {isClient && (
          <Select
            options={options}
            onChange={handleLocationChange}
            value={options.find(op => op.value === selectedLocation)}
          />
        )}
      </Header>
      <Spacing size={2}>
        {Object.entries(jobsByFunction).map(([functionName, functionJobs]) => (
          <Spacing key={functionName}>
            <Text bold>{functionName}</Text>
            <Spacing>
              {functionJobs
                .sort((a, b) => (a.title > b.title ? 1 : -1))
                .map(({ id, externalId, title, subtitle }) => (
                  <div key={id}>
                    <Link to={`/jobs/${externalId}`}>
                      <JobTitle>
                        {title}
                        {subtitle && ` (${subtitle})`}
                      </JobTitle>
                    </Link>
                  </div>
                ))}
            </Spacing>
          </Spacing>
        ))}
      </Spacing>
    </div>
  );
};
